import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { getOrganizationDashboard } from '@/utils/queryOptions';
import { Box, Center, HStack, Text, VStack } from '@chakra-ui/react';
import { DASHBOARD_RIGHT_PANEL_WIDTH_PX, MAX_CONTAINER_WIDTH } from '@/constants/dimensions';
import WholenessRingWithOverview from '@/components/organisms/WholenessRingWithOverview';
import OrgDashboardStatsItemBox from '@/modules/organization/OrgDashboardStatsItemBox';
import ArrowIconUp from '@/components/atoms/ArrowIconUp';
import ArrowIconDown from '@/components/atoms/ArrowIconDown';
import { formatDate } from 'date-fns';
import { useMemo, useState } from 'react';
import WholenessTrends from '@/components/molecules/WholenessTrends';
import { useAppStore } from '@/store/useAppStore';
import TabButtons from '@/components/atoms/TabButtons';
import OrganizationActiveContractPage from '@/components/organisms/OrganizationActiveContractPage';
import OrganizationDashboardSkeleton from '@/components/molecules/Skeletons/OrganizationDashboardSkeleton';
import TeamConcernsWidget from '@/modules/organization/TeamConcernsWidget';
import PeerSupportDashboardOverview from '@/modules/peer-support/PeerSupportDashboardOverview';
import OrganizationLogo from '@/components/molecules/OrganizationLogo';

const ORG_LOGO_CONTAINER_SIZE = 100;
const ORG_LOGO_PADDING = 20;
const ORG_LOGO_SIZE = ORG_LOGO_CONTAINER_SIZE - ORG_LOGO_PADDING;

const MAX_BAR_HEIGHT = 170;
const BAR_WIDTH = 32;

const OrganizationDashboard = () => {
  const { user } = useAppStore();

  const { data: organizationDashboard } = useSuspenseQuery(getOrganizationDashboard(user?.user_id));

  const organizationMonthlySessionsUtilization = useMemo(
    () => [...organizationDashboard.stats.sessionsUtilization.monthly].reverse(),
    [organizationDashboard],
  );

  const [currentTabSelection, setCurrentTabSelection] = useState<'home' | 'contract'>('home');

  return (
    <VStack width={MAX_CONTAINER_WIDTH} paddingBottom={'100px'} gap={0}>
      {/* Actual page content */}
      <VStack width={'100%'} marginBottom={'50px'}>
        {/* Org Logo and Name */}
        <HStack width={'100%'} justifyContent={'space-between'}>
          <HStack gap={'30px'}>
            <Center
              backgroundColor={'extra.white'}
              borderRadius={'50%'}
              boxShadow="0px 0px 12.5px 0px #00417926"
              width={`${ORG_LOGO_CONTAINER_SIZE}px`}
              minWidth={`${ORG_LOGO_CONTAINER_SIZE}px`}
              height={`${ORG_LOGO_CONTAINER_SIZE}px`}
              padding={`${ORG_LOGO_PADDING}px`}
            >
              <OrganizationLogo logoUrl={organizationDashboard.logoUrl} logoSize={ORG_LOGO_SIZE} />
            </Center>

            <Text variant={'urbanistTitleMedium'}>{organizationDashboard.name}</Text>
          </HStack>

          <Center>
            <TabButtons
              minWidth="120px"
              padding={'0 30px'}
              labelStyle={{ fontSize: '14px', lineHeight: '14px' }}
              options={[
                {
                  labelDisplay: 'WHOLENESS HOME',
                  labelValue: 'home',
                  onClick: () => setCurrentTabSelection('home'),
                },
                { labelDisplay: 'CONTRACT', labelValue: 'contract', onClick: () => setCurrentTabSelection('contract') },
              ]}
              selectedOptionValue={currentTabSelection}
            />
          </Center>
        </HStack>

        {currentTabSelection === 'home' ? (
          <>
            {/* Stats */}
            <HStack justifyContent={'space-between'} marginTop={'40px'} marginBottom={'86px'} width={'100%'}>
              <VStack width={'100%'} gap={'20px'}>
                <HStack width={'100%'} gap={'20px'}>
                  <OrgDashboardStatsItemBox
                    title={`Total\nScreeners`}
                    bottomLeftText={organizationDashboard.stats.totalScreeners.current}
                    bottomRightSmallText="Last 30 days"
                    trendIconJSX={
                      organizationDashboard.stats.totalScreeners.trend === 'DECLINING' ? (
                        <ArrowIconDown />
                      ) : (
                        <ArrowIconUp />
                      )
                    }
                  />
                  <OrgDashboardStatsItemBox
                    title="Total Members Signed in"
                    bottomLeftText={organizationDashboard.stats.totalMembersSignedIn.current}
                    bottomRightSmallText="Last 30 days"
                    trendIconJSX={
                      organizationDashboard.stats.totalMembersSignedIn.trend === 'DECLINING' ? (
                        <ArrowIconDown />
                      ) : (
                        <ArrowIconUp />
                      )
                    }
                  />
                  <OrgDashboardStatsItemBox
                    title="Member Care Sessions"
                    bottomLeftText={organizationDashboard.stats.memberCareSessions.current}
                    bottomRightSmallText="Last 30 days"
                    trendIconJSX={
                      organizationDashboard.stats.memberCareSessions.trend === 'DECLINING' ? (
                        <ArrowIconDown />
                      ) : (
                        <ArrowIconUp />
                      )
                    }
                  />
                </HStack>
                <HStack width={'100%'} gap={'20px'}>
                  {/* <OrgDashboardStatsItemBox
                    title="Unknown Risk (Unscreened)"
                    bottomLeftText={`${organizationDashboard.stats.unknownRisk}%`}
                    trendIconJSX={isOrgAdmin ? <UnscreenedRiskHoverIcon /> : undefined}
                  /> */}

                  <OrgDashboardStatsItemBox
                    title="Peer Support Requests"
                    bottomLeftText={organizationDashboard.stats.peerSupportRequests.current}
                    bottomRightSmallText="Last 30 days"
                    trendIconJSX={
                      organizationDashboard.stats.peerSupportRequests.trend === 'DECLINING' ? (
                        <ArrowIconDown />
                      ) : (
                        <ArrowIconUp />
                      )
                    }
                  />
                  <OrgDashboardStatsItemBox
                    title="Training Sessions Delivered"
                    bottomLeftText={organizationDashboard.stats.trainingSessions?.completed ?? 0}
                    bottomRightSmallText={`/ ${organizationDashboard.stats.trainingSessions?.total ?? 0} Total`}
                  />
                  <OrgDashboardStatsItemBox
                    title="Peer Support Certifications"
                    bottomLeftText={organizationDashboard.stats.peerSupportCertifications.current}
                    bottomRightSmallText={`/ ${organizationDashboard.stats.peerSupportCertifications.total} Total`}
                  />
                </HStack>
              </VStack>

              <VStack
                minWidth={DASHBOARD_RIGHT_PANEL_WIDTH_PX}
                height={'276px'}
                backgroundColor={'extra.white'}
                boxShadow="0px 8px 34px 0px #0041791A"
                borderRadius={'14px'}
                padding={'20px'}
              >
                <HStack width={'100%'} justifyContent={'space-between'}>
                  <Text variant={'urbanistBoldLarge'}>Sessions Utilization</Text>
                  <Text variant={'urbanistMediumRegular'} color={'text.mediumGray'}>
                    {organizationDashboard.stats.sessionsUtilization.totalUsed} Used /{' '}
                    {organizationDashboard.stats.sessionsUtilization.totalAvailable} Purchased
                  </Text>
                </HStack>

                <HStack height={'100%'} gap={'15px'}>
                  {organizationMonthlySessionsUtilization.length === 0 ? (
                    <Text variant={'urbanistBoldLarge'}>No utilization data available</Text>
                  ) : (
                    organizationMonthlySessionsUtilization.map((sessionMonthData) => (
                      <VStack
                        key={`${sessionMonthData.year}-${sessionMonthData.month}`}
                        height={'100%'}
                        justifyContent={'end'}
                      >
                        <VStack
                          backgroundColor={'primary.500'}
                          borderRadius={'10px'}
                          gap={0}
                          height={
                            organizationDashboard.stats.sessionsUtilization.totalUsed === 0 &&
                            organizationDashboard.stats.sessionsUtilization.totalAvailable === 0
                              ? 50
                              : `${Math.min((MAX_BAR_HEIGHT * sessionMonthData.used) / sessionMonthData.purchased, MAX_BAR_HEIGHT)}px`
                          }
                          width={`${BAR_WIDTH}px`}
                        />
                        <HStack maxWidth={`${BAR_WIDTH + 4}px`} gap="4px">
                          <Text variant={'urbanistSmallBold'} color={'text.mediumGray'}>
                            {formatDate(new Date(2025, sessionMonthData.month - 1), 'MMM')}
                          </Text>
                          <Text variant={'urbanistSmallBold'}>{sessionMonthData.used}</Text>
                        </HStack>
                      </VStack>
                    ))
                  )}
                </HStack>
              </VStack>
            </HStack>

            {/* Wholeness Ring with Overview  &  WholenessTrends  */}
            <VStack width={'100%'} gap={'100px'}>
              <WholenessRingWithOverview
                isOrganizationOverview
                isHoverable
                dashboardScores={organizationDashboard.score}
                subtitle="This graph displays the total scores of your team who have taken the screener over the past 30 days."
                title="Wholeness Ring"
                isSendScreenerReminderButtonShown={true}
                wholenessObservation={organizationDashboard.wholenessObservation}
                organizationName={organizationDashboard.name}
              />

              <HStack width={'100%'} alignItems={'center'} gap={'75px'}>
                <Box flex={1} minWidth={0}>
                  <WholenessTrends
                    dashboardTrendData={organizationDashboard.insights ? organizationDashboard.trends : null}
                    progressData={organizationDashboard.progress}
                  />
                </Box>
                <Box width={'380px'} flexShrink={0}>
                  <TeamConcernsWidget />
                </Box>
              </HStack>

              <PeerSupportDashboardOverview canLogContact={false} />
            </VStack>
          </>
        ) : (
          <OrganizationActiveContractPage />
        )}
      </VStack>
    </VStack>
  );
};

export const Route = createFileRoute('/_authenticated/_organization/organization/dashboard')({
  staticData: { title: 'Organization Dashboard' },
  component: OrganizationDashboard,
  loader: async ({ context }) =>
    context.queryClient.ensureQueryData(getOrganizationDashboard(context.auth?.user?.user_id)),
  // NOTE: Default pending component can be overridden if you want to show a custom pending states. This is just a showcase
  pendingComponent: () => <OrganizationDashboardSkeleton />,
});
